import React, { useEffect, useState } from "react";
import { getSettings, updateSettings, restoreOnboarding } from "@/lib/apiClient";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AlertSettings } from "@/components/AlertSettings";
import { CurrencySettings } from "@/components/CurrencySettings";
import { TeamCard } from "@/components/TeamCard";
import { toast } from "sonner";
import { Loader2, Zap, BookOpen, Mail, Users, Landmark, Smartphone, Scale, ExternalLink, ShieldCheck } from "lucide-react";
import { LEGAL_DOCS, LEGAL_UPDATED } from "@/legal/content";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PhoneCard } from "@/components/PhoneCard";
import { ChartOfAccountsEditor } from "@/components/ChartOfAccountsEditor";
import { AccountingTab } from "@/components/AccountingTab";
import { RuleSimulator } from "@/components/RuleSimulator";
import { ApprovalRulesCard, SupplierEmailsCard } from "@/components/SettingsCards";



const TABS = [
  { key: "rules", label: "Approval rules", icon: Zap },
  { key: "coa", label: "Chart of Accounts", icon: BookOpen },
  { key: "xero", label: "Accounting", icon: Landmark },
  { key: "alerts", label: "Alerts & emails", icon: Mail },
  { key: "team", label: "Team & currency", icon: Users },
  { key: "phone", label: "Phone", icon: Smartphone },
];

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const { updateBaseCurrency } = useApp();
  const hashTab = location.hash === "#team" ? "team" : location.hash === "#xero" ? "xero" : null;
  const requested = params.get("tab") === "accounting" ? "xero" : params.get("tab");
  const tab = TABS.some((t) => t.key === requested) ? requested : hashTab || "rules";
  const setTab = (v) => { params.set("tab", v); setParams(params, { replace: true }); };

  useEffect(() => {
    getSettings().then(setS);
  }, []);

  const save = async (label = "Approval rules saved") => {
    setSaving(true);
    try {
      const saved = await updateSettings({
        auto_approve_enabled: s.auto_approve_enabled,
        auto_approve_max_amount: Number(s.auto_approve_max_amount) || 0,
        auto_approve_min_confidence: Number(s.auto_approve_min_confidence) || 0,
        notify_received: !!s.notify_received,
        notify_approved: !!s.notify_approved,
        notify_paid: !!s.notify_paid,
        base_currency: s.base_currency,
        auto_approve_in_base: !!s.auto_approve_in_base,
        alerts_enabled: !!s.alerts_enabled,
        forwarding_alerts: s.forwarding_alerts !== false,
        alert_emails: s.alert_emails || [],
        sender_whitelist_enabled: !!s.sender_whitelist_enabled,
        trust_supplier_contacts: !!s.trust_supplier_contacts,
        review_sla_hours: Number(s.review_sla_hours) || 48,
        sla_nudge_email: !!s.sla_nudge_email,
        escalation_reviewer: s.escalation_reviewer || "Sarah Jenkins",
        escalation_after_hours: Number(s.escalation_after_hours) || 24,
        close_pack_auto_email: s.close_pack_auto_email !== false,
        close_pack_schedule_enabled: s.close_pack_schedule_enabled !== false,
        close_pack_schedule_day: Number(s.close_pack_schedule_day) || 1,
        close_pack_schedule_hour: Number.isFinite(Number(s.close_pack_schedule_hour)) ? Number(s.close_pack_schedule_hour) : 7,
      });
      setS(saved);
      updateBaseCurrency(saved.base_currency);
      toast.success(label);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!s) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Rules & Accounts</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Approval rules, your chart of accounts, accounting system, alerts and your team — one tab each.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 bg-muted/60 p-1" data-testid="settings-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <TabsTrigger key={key} value={key} data-testid={`settings-tab-${key}`} className="gap-1.5 px-3 py-1.5 text-sm data-[state=active]:shadow-sm">
              <Icon className="h-4 w-4" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="rules" className="mt-6 space-y-6" data-testid="settings-panel-rules">
          <div className="grid gap-6 lg:grid-cols-2 items-start">
            <ApprovalRulesCard s={s} setS={setS} onSave={save} saving={saving} />
            <RuleSimulator minConfidence={s.auto_approve_min_confidence} maxAmount={s.auto_approve_max_amount} enabled={s.auto_approve_enabled} />
          </div>
        </TabsContent>

        <TabsContent value="coa" className="mt-6" data-testid="settings-panel-coa">
          <ChartOfAccountsEditor />
        </TabsContent>

        <TabsContent value="xero" className="mt-6" data-testid="settings-panel-xero">
          <AccountingTab />
        </TabsContent>

        <TabsContent value="alerts" className="mt-6 space-y-6" data-testid="settings-panel-alerts">
          <AlertSettings s={s} setS={setS} onSave={save} saving={saving} />
          <SupplierEmailsCard s={s} setS={setS} onSave={save} saving={saving} />
        </TabsContent>

        <TabsContent value="phone" className="mt-6" data-testid="settings-panel-phone">
          <PhoneCard />
        </TabsContent>

        <TabsContent value="team" className="mt-6 space-y-6" data-testid="settings-panel-team">
          <TeamCard />
          <Link to="/settings/permissions" className="group flex items-center gap-4 rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors" data-testid="settings-permissions-link">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><ShieldCheck className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="block font-semibold group-hover:text-primary">Permissions & Policies</span>
              <span className="block text-sm text-muted-foreground">Admin-only controls: what each team member can do, approval thresholds, duplicate and VAT rules, retention and audit requirements.</span>
            </span>
            <ExternalLink className="h-4 w-4 text-muted-foreground rotate-45 group-hover:text-primary" />
          </Link>
          <CurrencySettings s={s} setS={setS} onSave={save} saving={saving} />
        </TabsContent>
      </Tabs>

      <section className="rounded-xl border border-border bg-card p-5" data-testid="settings-legal">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold"><Scale className="h-4 w-4 text-primary" /> Legal</h2>
            <p className="mt-1 text-sm text-muted-foreground">Our public policies and agreements — Privacy Policy, Terms of Service, Cookie Policy, Security and the Data Processing Agreement. Last updated {LEGAL_UPDATED}.</p>
          </div>
          <a href="/legal" target="_blank" rel="noreferrer" data-testid="settings-legal-open"
             className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted">
            Open Legal centre <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {LEGAL_DOCS.map((d) => (
            <a key={d.slug} href={`/legal/${d.slug}`} target="_blank" rel="noreferrer" data-testid={`settings-legal-${d.slug}`}
               className="rounded-full border border-border px-3 py-1 text-xs hover:border-primary hover:text-primary">{d.title}</a>
          ))}
        </div>
      </section>

      <p className="text-xs text-muted-foreground">
        New here?{" "}
        <button data-testid="restore-checklist-btn" className="text-primary hover:underline"
                onClick={() => restoreOnboarding().then(() => { toast.success("Checklist restored on your Dashboard"); navigate("/"); })}>
          Show the getting-started checklist on the Dashboard
        </button>
      </p>
    </div>
  );
}
